import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import ExperienceCard from "@/components/base/ExperienceCard";
import CTAPanel from "@/components/base/CTAPanel";
import RelatedContent from "@/components/feature/RelatedContent";

const ssaAreas = [
  "Enterprise infrastructure support",
  "LAN/WAN engineering",
  "Server and collaboration support",
  "VMware / virtualization",
  "Database engineering",
  "Database administration",
  "Oracle / SQL platform support",
  "Enterprise software support",
  "Platform engineering / support",
  "WebSphere and associated software",
  "Application-related technical services",
  "Web / application services",
  "SharePoint-related services",
  "Specialist technical resources",
];

const cdcAreas = [
  "Microsoft Power Platform",
  "Power Apps",
  "Power Automate",
  "Power BI",
  "SharePoint",
  "Workflow solutions",
  "Requirements analysis",
  "Solution design",
  "Integration",
  "Testing",
  "Implementation",
  "Training",
  "Supporting web / database technologies",
];

const federalSummaries = [
  {
    label: "Infrastructure & Network",
    detail: "LAN/WAN engineering, server and collaboration support, VMware, and virtualization within federal enterprise environments.",
  },
  {
    label: "Database & Data",
    detail: "Oracle and SQL Server database engineering, administration, schema management, and platform support.",
  },
  {
    label: "Enterprise Platforms",
    detail: "WebSphere and associated software support, platform engineering, and application-related technical services.",
  },
  {
    label: "Microsoft & Web",
    detail: "SharePoint-related services, web/application services, Power Platform, and workflow solutions.",
  },
];

const stateLocalThemes = [
  {
    title: "Application Integration",
    description: "Documented application integration within state and local government technology environments, connecting systems and workflows across public-sector programs.",
    icon: "ri-node-tree",
  },
  {
    title: "Digital Solutions",
    description: "Support for public-sector digital solutions including web-based applications and portal environments serving agency stakeholders.",
    icon: "ri-window-line",
  },
  {
    title: "Database-Backed Systems",
    description: "Engineering and support for database-backed applications used in state and local government operations.",
    icon: "ri-database-2-line",
  },
  {
    title: "Workflow Modernization",
    description: "Modernization of manual and legacy workflows into structured, technology-enabled processes for agency operations.",
    icon: "ri-flow-chart",
  },
];

const stateLocalAreas = [
  "Application integration",
  "Web-based applications",
  "Portal environments",
  "Database-backed systems",
  "Workflow modernization",
  "Digital solutions",
  "Requirements & design",
  "Implementation & testing",
  "Production support",
];

export default function UsaGovernmentExperience() {
  return (
    <Layout>
      <PageHero
        title="USA Government Experience"
        subtitle="Experience · U.S. Government"
        description="Mitaja Corporation provides technology delivery across U.S. federal, state, and local government environments — spanning enterprise infrastructure, network engineering, database systems, Microsoft platforms, application integration, and public-sector digital solutions."
        size="lg"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <ExperienceCard
            client="Social Security Administration (SSA)"
            country="United States"
            icon="ri-community-line"
            project="Enterprise Technology Support Across Federal IT Environments"
            scope="Mitaja Corporation provided subcontract technology services supporting federal enterprise technology environments, including enterprise infrastructure support, network engineering, database engineering and administration, enterprise platform support, and specialist technical resources."
            capabilities={[
              "Enterprise Infrastructure",
              "LAN/WAN Engineering",
              "Database Engineering & Administration",
              "Enterprise Platform Support",
              "Web & Application Services",
              "Specialist Technical Resources",
            ]}
            technologies={[
              "VMware",
              "vCenter",
              "Virtualization",
              "Windows Server",
              "Oracle",
              "SQL Server",
              "WebSphere",
              "SharePoint",
            ]}
          />

          <div className="mt-8">
            <div className="card-base p-6 md:p-8 bg-background-100 border-background-200/80">
              <h4 className="text-sm font-bold text-primary-800 uppercase tracking-wider mb-4">
                SSA Supported Experience Areas
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
                {ssaAreas.map((area) => (
                  <div key={area} className="flex items-start gap-2 text-sm text-foreground-700 leading-snug">
                    <i className="ri-check-line text-primary-600 mt-0.5 shrink-0"></i>
                    {area}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-14">
            <ExperienceCard
              client="Centers for Disease Control and Prevention (CDC)"
              country="United States"
              icon="ri-health-book-line"
              project="Microsoft Power Platform & Workflow Solutions"
              scope="Mitaja Corporation provided subcontract technology services supporting a Microsoft Power Platform initiative, including requirements analysis, solution design, integration, testing, implementation, and training across supported applications."
              capabilities={[
                "Microsoft Power Platform",
                "Workflow Solutions",
                "Requirements Analysis",
                "Solution Design",
                "Integration",
                "Testing & Implementation",
                "Training",
              ]}
              technologies={[
                "Power Apps",
                "Power Automate",
                "Power BI",
                "SharePoint",
                "Web / Database Technologies",
              ]}
            />
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page">
          <SectionHeader
            title="Federal Delivery Themes"
            description="Capability areas delivered through subcontract engagements supporting federal enterprise programs."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {federalSummaries.map((theme) => (
              <div key={theme.label} className="card-base p-6">
                <h3 className="text-lg font-bold text-foreground-900 mb-2">
                  {theme.label}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {theme.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="State & Local Delivery Themes"
            description="Capability areas delivered within U.S. state and local government technology programs."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stateLocalThemes.map((theme) => (
              <div key={theme.title} className="card-base p-6">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <i className={`${theme.icon} text-primary-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">
                  {theme.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {theme.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-background-100">
        <div className="container-page">
          <SectionHeader
            title="Supported Service Areas"
            description="Capability areas delivered across federal, state, and local government technology programs."
          />
          <div className="flex flex-wrap justify-center gap-3">
            {stateLocalAreas.map((area) => (
              <span key={area} className="text-sm px-4 py-2 rounded-full bg-white text-primary-700 font-medium border border-primary-100">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      <RelatedContent
        subtitle="Supporting Capabilities"
        title="How This Experience Maps to Mitaja's Services"
        description="Connect documented federal, state, and local government experience to the capabilities Mitaja delivers."
        links={[
          {
            label: "Infrastructure & Network Engineering",
            description: "LAN/WAN engineering, server and collaboration support, VMware, virtualization, and modernization.",
            href: "/capabilities/infrastructure-network-engineering",
            icon: "ri-server-line",
          },
          {
            label: "Database & Data Platform Services",
            description: "Oracle and SQL Server engineering, administration, schema management, and platform support.",
            href: "/capabilities/database-data-platform",
            icon: "ri-database-2-line",
          },
          {
            label: "Capability Statement & Contracting Info",
            description: "Review Mitaja's capability statement, UEI, CAGE, NAICS codes, and contracting credentials.",
            href: "/resources/capability-statement",
            icon: "ri-file-list-line",
          },
        ]}
      />

      <CTAPanel
        title="Supporting U.S. Government Missions"
        description="Mitaja delivers qualified technical resources and engineering services for federal, state, and local programs through prime-contractor partnerships."
        secondaryAction={{ label: "View Experience Overview", href: "/experience" }}
        variant="dark"
      />
    </Layout>
  );
}